// src/components/reports/MonthlySummaryChart.tsx
import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ReferenceLine, Label
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MonthlyRollup, BudgetVsActualPoint } from '../../types/ReportsTrends';
import { useFormatting } from '../../context/FormattingContext';
import { formatMoney } from '../../utils/formatting';

type Props = {
  data: MonthlyRollup[];
  comparePrev: boolean;
  month: string;
  bvaSeries: BudgetVsActualPoint[];
  savings?: { inRange: number; cumulative: number };
};

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; name?: string }>;
  label?: string;
  formatCurrency: (value?: number) => string;
}
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, formatCurrency }) => {
  if (!active || !payload || payload.length === 0) return null;
  const vals: Record<string, number> = {};
  payload.forEach(p => {
    if (typeof p.value === 'number') vals[p.dataKey] = p.value;
  });
  return (
    <div style={{ background: '#fff', border: '1px solid #ddd', padding: 10, borderRadius: 8 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div>Income: {formatCurrency(vals.income)}</div>
      <div>Expenses: {formatCurrency(vals.expense)}</div>
      <div>Transfers: {formatCurrency(vals.savings)}</div>
      <div style={{ marginTop: 6, fontWeight: 600 }}>Net: {formatCurrency(vals.net)}</div>
      {'budgeted' in vals && (
        <div style={{ marginTop: 6 }}>
          Budget: {formatCurrency(vals.budgeted)}{' '}
          {'variance' in vals && (
            <span style={{ marginLeft: 8, color: (vals.variance ?? 0) >= 0 ? '#2e7d32' : '#c62828' }}>
              {(vals.variance ?? 0) >= 0 ? 'Under' : 'Over'} by {formatCurrency(Math.abs(vals.variance))}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const MonthlySummaryChart: React.FC<Props> = ({ data, comparePrev, month, bvaSeries, savings }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const formatting = useFormatting();
  const fmtCurrency = (v?: number) =>
    typeof v === 'number' ? formatMoney(v, formatting, { maximumFractionDigits: 0 }) : '–';
  const fmtCompactCurrency = (v?: number) => {
    if (typeof v !== 'number') return '–';
    try {
      return new Intl.NumberFormat(formatting.locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(v);
    } catch {
      return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(v);
    }
  };

  const prevKey = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(Date.UTC(y, m - 2, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  }, [month]);

  const current = data.find(d => d.month === month);
  const prev = data.find(d => d.month === prevKey);
  const bvaCurrent = bvaSeries.find(s => s.month === month);

  const rows = useMemo(() => {
    const arr: Array<{
      label: string;
      income: number;
      expense: number;
      savings: number;
      net: number;
      budgeted?: number;
      variance?: number;
    }> = [];

    if (current) {
      arr.push({
        label: 'This Month',
        income: current.income,
        expense: current.expense,
        savings: current.savings,
        net: current.net,
        budgeted: bvaCurrent?.budgeted,
        variance: bvaCurrent?.variance,
      });
    }
    if (comparePrev && prev) {
      arr.push({
        label: 'Prev Month',
        income: prev.income,
        expense: prev.expense,
        savings: prev.savings,
        net: prev.net,
      });
    }
    return arr;
  }, [current, prev, comparePrev, bvaCurrent]);

  if (!rows.length) return <div style={{ padding: 12, opacity: 0.7 }}>No data for this month.</div>;

  const maxY = Math.max(
    ...rows.flatMap(r => [r.income, r.expense, r.savings, r.budgeted ?? 0].filter(n => typeof n === 'number')) as number[]
  );
  const yDomain: [number, number] = [0, Math.max(1, Math.ceil(maxY * 1.15))];
  const budgetY = rows[0]?.budgeted;

  return (
    <div style={{ width: '100%', height: isSmallScreen ? 300 : 360 }}>
      <ResponsiveContainer>
        <ComposedChart
          data={rows}
          barCategoryGap={24}
          margin={{ top: 8, right: isSmallScreen ? 4 : 12, left: 0, bottom: isSmallScreen ? 2 : 8 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" tick={{ fontSize: isSmallScreen ? 11 : 12 }} />
          <YAxis
            domain={yDomain}
            width={isSmallScreen ? 52 : 60}
            tick={{ fontSize: isSmallScreen ? 11 : 12 }}
            tickFormatter={(n: number) => (isSmallScreen ? fmtCompactCurrency(n) : fmtCurrency(n))}
          />
          <Tooltip content={<CustomTooltip formatCurrency={fmtCurrency} />} />
          <Legend wrapperStyle={{ fontSize: isSmallScreen ? 11 : 12 }} iconSize={isSmallScreen ? 10 : 14} />

          {/* Distinct theme-based colors */}
          <Bar dataKey="income"  name="Income"   fill={theme.palette.success.main} radius={[6,6,0,0]} />
          <Bar dataKey="expense" name="Expenses" fill={theme.palette.error.main}   radius={[6,6,0,0]} />
          <Bar dataKey="savings" name="Transfers"  fill={theme.palette.info.main}    radius={[6,6,0,0]} />

          <Line type="monotone" dataKey="net" name="Net" dot stroke={theme.palette.text.primary} strokeWidth={2} />

          {typeof budgetY === 'number' && budgetY > 0 && (
            <ReferenceLine y={budgetY} stroke={theme.palette.warning.main} strokeDasharray="6 4">
              <Label position="right" value={`Budget ${fmtCurrency(budgetY)}`} />
            </ReferenceLine>
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {savings && (
        <div style={{ fontSize: isSmallScreen ? 11 : 12, opacity: 0.75, marginTop: 6 }}>
          Savings this period: {fmtCurrency(savings.inRange)} · Cumulative savings: {fmtCurrency(savings.cumulative)}
        </div>
      )}
    </div>
  );
};

export default MonthlySummaryChart;
